import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHero from '@/components/feature/PageHero';
import CtaSection from '@/components/feature/CtaSection';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import Counter from '@/components/base/Counter';
import { company, values } from '@/mocks/site';

const pillars = [
  {
    icon: 'ri-focus-3-line',
    title: 'Notre mission',
    text: "Transformer le paysage industriel camerounais en apportant des solutions fiables dans des secteurs clés, avec l'expertise technique et l'innovation comme fil conducteur.",
  },
  {
    icon: 'ri-route-line',
    title: 'Notre trajectoire',
    text: "Des premiers ateliers de soudure aux ouvrages chaudronnés les plus complexes, nous avons construit notre savoir-faire projet après projet, sans jamais sacrifier la sécurité.",
  },
  {
    icon: 'ri-global-line',
    title: 'Notre portée',
    text: "Une clientèle variée, des entreprises locales aux multinationales, au Cameroun et dans la sous-région, avec des exigences strictes et des projets de haute envergure.",
  },
];

const commitments = [
  "Analyse des risques et plan de prévention avant chaque intervention",
  "Équipements de protection individuelle et collectifs systématiques",
  "Soudeurs et monteurs formés aux procédures de travail en hauteur",
  "Traçabilité des matériaux, des paramètres de soudage et des contrôles",
];

const aboutStats = [
  { value: 10, suffix: '', label: "Domaines d'expertise" },
  { value: 3, suffix: '', label: 'Métiers historiques' },
  { value: 100, suffix: '%', label: 'Contrôle qualité' },
];

export default function About() {
  return (
    <SiteLayout>
      <PageHero
        breadcrumb="À propos"
        label="Notre histoire"
        title="Construire l'industrie, transmettre le métier"
        description="SGSCTIC s'impose comme un partenaire des travaux industriels lourds au Cameroun : une équipe qualifiée, un atelier outillé et une exigence constante sur la sécurité comme sur la qualité."
        image="https://readdy.ai/api/search-image?query=Industrial%20welding%20team%20in%20navy%20blue%20coveralls%20and%20orange%20safety%20helmets%20working%20inside%20a%20metal%20fabrication%20workshop%2C%20glowing%20sparks%20from%20an%20arc%20weld%2C%20steel%20beams%20and%20large%20diameter%20pipes%20stacked%20in%20the%20background%2C%20warm%20directional%20light%20with%20soft%20haze%2C%20cinematic%20editorial%20photography%2C%20shallow%20depth%20of%20field%2C%20wide%20balanced%20composition%20with%20open%20space%20for%20a%20headline%2C%20high%20detail&width=1920&height=1080&seq=sgsctic-about-hero-v2&orientation=landscape"
        imageAlt="Atelier SGSCTIC : soudeurs en combinaison sur une structure métallique à Douala"
      />

      {/* Story */}
      <section className="bg-background-50 py-20 md:py-28">
        <div className="container-wide">
          <div className="grid items-start gap-14 lg:grid-cols-[0.95fr_1.05fr] lg:gap-20">
            <Reveal variant="fade">
              <div className="relative">
                <div className="aspect-[4/5] w-full overflow-hidden rounded-lg bg-background-200">
                  <img
                    src="https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2025-12-19-at-10.05.26-1.jpeg"
                    alt="Chaudronnier SGSCTIC travaillant sur une structure métallique"
                    title="Chaudronnerie industrielle à Douala"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-px overflow-hidden rounded-lg bg-background-200">
                  {aboutStats.map((stat) => (
                    <div key={stat.label} className="bg-background-100 px-4 py-5 text-center">
                      <p className="font-heading text-3xl font-semibold leading-none text-foreground-950">
                        <Counter value={stat.value} suffix={stat.suffix} />
                      </p>
                      <p className="mt-2 text-[10px] uppercase leading-tight tracking-[0.14em] text-foreground-500">
                        {stat.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <div>
              <Reveal>
                <SectionHeading
                  label="Depuis nos débuts"
                  title={
                    <>
                      Une entreprise née
                      <br />
                      sur le terrain
                    </>
                  }
                />
              </Reveal>
              <Reveal delay={120}>
                <p className="mt-7 text-base leading-relaxed text-foreground-600">
                  SGSCTIC possède une expertise de plusieurs années dans le domaine de la soudure,
                  de la chaudronnerie et de la tuyauterie. Nous fournissons des services de haute
                  qualité aux industries du Cameroun et de la sous-région, avec une équipe dédiée à
                  l'excellence et à la satisfaction de nos clients.
                </p>
                <p className="mt-4 text-base leading-relaxed text-foreground-600">
                  L'entreprise a été fondée avec l'ambition de transformer le paysage industriel
                  camerounais, en apportant des solutions fiables dans des secteurs clés. Depuis nos
                  débuts, nous avons toujours misé sur l'expertise technique et l'innovation.
                </p>
                <p className="mt-4 text-base leading-relaxed text-foreground-600">
                  Nous avons eu le privilège de servir une clientèle variée, allant des entreprises
                  locales aux multinationales, en répondant à des exigences strictes et en livrant
                  des projets de haute envergure.
                </p>
              </Reveal>

              <div className="mt-9 space-y-4">
                {commitments.map((item, index) => (
                  <Reveal key={item} delay={180 + index * 80}>
                    <div className="flex items-start gap-4 rounded-md border border-background-200 bg-background-100 px-5 py-4">
                      <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50">
                        <i className="ri-check-line text-sm" />
                      </span>
                      <p className="text-sm leading-relaxed text-foreground-800">{item}</p>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={520}>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <Link
                    to="/offres"
                    className="btn-shine flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-6 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-background-50 transition-colors hover:bg-primary-600"
                  >
                    Nos offres
                    <i className="ri-arrow-right-line text-base" />
                  </Link>
                  <a
                    href={company.phoneHref}
                    className="flex items-center gap-2 whitespace-nowrap text-sm font-semibold uppercase tracking-[0.1em] text-foreground-800 transition-colors hover:text-primary-600"
                  >
                    <i className="ri-phone-line text-primary-500" />
                    {company.phone}
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Mission pillars */}
      <section className="bg-background-100 py-20 md:py-24">
        <div className="container-wide">
          <Reveal>
            <p className="mx-auto max-w-4xl text-center font-heading text-2xl font-medium uppercase leading-tight tracking-wide text-foreground-900 md:text-4xl">
              «&nbsp;Notre engagement envers la sécurité et la qualité est au cœur de toutes nos
              opérations.&nbsp;»
            </p>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-lg bg-background-200 md:grid-cols-3">
            {pillars.map((pillar, index) => (
              <Reveal key={pillar.title} delay={index * 110} className="bg-background-50">
                <div className="group h-full p-7 md:p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-secondary-100 text-secondary-900 transition-colors duration-300 group-hover:bg-primary-500 group-hover:text-background-50">
                    <i className={`${pillar.icon} text-xl`} />
                  </span>
                  <h3 className="mt-5 font-heading text-2xl font-semibold uppercase text-foreground-950">
                    {pillar.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{pillar.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-background-50 py-20 md:py-28">
        <div className="container-wide">
          <Reveal>
            <SectionHeading
              label="Nos valeurs"
              title="Les principes qui guident nos chantiers"
              description="Découvrez les principes fondamentaux qui guident SGSCTIC dans ses actions et son développement."
            />
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((value, index) => (
              <Reveal key={value.title} delay={index * 90}>
                <div className="group h-full rounded-lg border border-background-200 bg-background-100/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 hover:bg-background-50">
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500/10 text-primary-600">
                      <i className={`${value.icon} text-xl`} />
                    </span>
                    <span className="font-label text-[10px] tracking-[0.2em] text-foreground-400">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-semibold uppercase text-foreground-950">
                    {value.title}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{value.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Safety split */}
      <section className="relative overflow-hidden bg-foreground-950 py-20 md:py-24">
        <div className="absolute inset-0 hatch-lines-light opacity-25" />
        <div className="container-wide relative z-10 grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-primary-500" />
              <span className="label-mono text-primary-300">Culture HSE</span>
            </div>
            <h2 className="display-title mt-5 text-4xl text-background-50 md:text-5xl">
              La sécurité n'est pas
              <br />
              une option
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-background-300">
              Sur un site industriel, chaque geste compte. Nos équipes appliquent des protocoles
              stricts, du balisage de la zone de travail à la vérification des équipements de levage.
              L'objectif est simple : zéro incident, à chaque intervention.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {[
                { icon: 'ri-shield-star-line', text: 'EPI complets et contrôlés' },
                { icon: 'ri-fire-line', text: 'Permis feu et surveillance' },
                { icon: 'ri-barricade-line', text: 'Balisage et plan de prévention' },
                { icon: 'ri-clipboard-line', text: 'Checklists avant démarrage' },
              ].map((item) => (
                <div
                  key={item.text}
                  className="flex items-center gap-3 border border-background-50/15 px-4 py-3"
                >
                  <i className={`${item.icon} text-primary-400`} />
                  <span className="text-sm text-background-200">{item.text}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal variant="fade" delay={120}>
            <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-background-50/15">
              <img
                src="https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2025-12-19-at-10.05.23.jpeg"
                alt="Équipe SGSCTIC en équipement de protection sur chantier"
                title="Sécurité sur chantier industriel"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection
        title="Envie de travailler"
        accent="avec une équipe solide ?"
        description="Que vous soyez donneur d'ordre, bureau d'études ou entreprise industrielle, nous étudions votre besoin et vous répondons avec une proposition technique claire."
      />
    </SiteLayout>
  );
}